import {
  resolveNotificationHref as resolveHref,
  type NotificationRouteMap,
} from '@gokrew/ui/utility/notifications';
import {
  NotificationAudience,
  NotificationSubjectType,
  NotificationType,
  type NotificationItem,
} from '@gokrew/models/notifications';
import {
  EmployeeChangeRequestStatus,
  EmployeeSection,
} from '@gokrew/models/employee-change-requests';
import { DETAILS_REQUESTS_VIEW_PARAM } from '@gokrew/ui/hooks';

/**
 * The employee's own page for each change-request section.
 * Keyed by every section, so a new one fails the build until it has a page.
 */
const CHANGE_REQUEST_PAGE: Record<EmployeeSection, string> = {
  [EmployeeSection.Document]: '/my-information/documents',
  [EmployeeSection.Education]: '/my-information/education',
  [EmployeeSection.Family]: '/my-information/family',
  [EmployeeSection.BankAccount]: '/my-information/banking',
  [EmployeeSection.PersonalInformation]: '/my-information',
};

/**
 * Employee-portal route per subject type. The backend deliberately sends no
 * URLs, so this map is the portal's own. A subject type missing from it — a
 * newly shipped one, or an admin-only resource this portal has no screen for
 * (leave policies, branches, bulk imports, …) — renders as a non-clickable
 * item.
 *
 * The one approval queue in this portal is My team > Approvals (leave requests
 * a manager or delegate decides); elsewhere `audience` picks between the
 * reader's own record and nothing at all.
 */
const SUBJECT_ROUTE: NotificationRouteMap = {
  [NotificationSubjectType.MESSAGING_CONVERSATION]: (id) =>
    `/messages?conversation=${encodeURIComponent(id)}`,
  // An approver's copy (a reporting manager or delegate deciding someone
  // else's leave) opens that request in My team > Approvals; the reader's
  // own request opens their leave page.
  [NotificationSubjectType.LEAVE_REQUEST]: (id, item) =>
    item.audience === NotificationAudience.APPROVER
      ? `/my-team/approvals?request=${encodeURIComponent(id)}`
      : '/leave-management',
  [NotificationSubjectType.LEAVE_WALLET]: () => '/leave-management',
  // A direct grant's subject is the grant itself, not a request — both land on
  // the overview, which is where this portal shows balances.
  [NotificationSubjectType.LEAVE_GRANT]: () => '/leave-management',
  [NotificationSubjectType.HOLIDAY]: () => '/leave-management/calendar',
  [NotificationSubjectType.HOLIDAY_CALENDAR]: () =>
    '/leave-management/calendar',
  // A workflow step reminder to the manager of the employee it is about
  // opens that employee in My team; any other task copy, the reader's tasks.
  [NotificationSubjectType.TASK]: (_id, item) => {
    const employeeId = item.context?.flowEmployeeId;
    return item.audience === NotificationAudience.WATCHER &&
      typeof employeeId === 'string'
      ? `/my-team/members/${encodeURIComponent(employeeId)}`
      : '/tasks';
  },
  [NotificationSubjectType.TASK_OCCURRENCE]: () => '/tasks',
  [NotificationSubjectType.TASK_ASSIGNMENT]: () => '/tasks',
  [NotificationSubjectType.SURVEY]: (id) => `/surveys/${id}`,
  // A manager reviewer's request or reminder opens the review they write
  // (performance spec PF-2); a self or peer review opens the form. A daily
  // digest of several reviews opens the list they are in (PF-23).
  [NotificationSubjectType.PERFORMANCE_REVIEW_SUBMISSION]: (id, item) => {
    const manager = item.context?.reviewerType === 'MANAGER';
    if (Number(item.context?.count) > 1) {
      return manager ? '/my-team/performance' : '/performance/reviews';
    }
    return manager && typeof item.context?.reviewId === 'string'
      ? `/performance/manager-reviews/${item.context.reviewId}`
      : `/performance/reviews/submissions/${id}`;
  },
  // The reviewee's own review once shared; the manager is told on the review
  // they write that the self review is in, or that it was acknowledged.
  // Team feedback on a manager HR shared opens its grouped results.
  [NotificationSubjectType.PERFORMANCE_REVIEW]: (id, item) =>
    item.context?.teamFeedback
      ? `/performance/team-feedback/${id}`
      : item.type === NotificationType.PERFORMANCE_REVIEW_ACKNOWLEDGED ||
          item.type === NotificationType.PERFORMANCE_SELF_REVIEW_SUBMITTED
        ? `/performance/manager-reviews/${id}`
        : `/performance/reviews/${id}`,
  // A goal set for me, or the check-in reminder: My performance > Goals.
  [NotificationSubjectType.PERFORMANCE_GOAL]: (id) =>
    `/performance/goals?goal=${encodeURIComponent(id)}`,
  [NotificationSubjectType.ONE_ON_ONE]: (id) =>
    `/performance/one-on-ones/${id}`,
  [NotificationSubjectType.ANNOUNCEMENT]: () => '/dashboard',
  // A manager's copy (a reporting manager, an escalated manager or a
  // delegate deciding someone else's timesheet) opens it in My team >
  // Approvals; the reader's own timesheet opens their timesheet page.
  [NotificationSubjectType.TIMESHEET_REQUEST]: (id, item) =>
    item.audience === NotificationAudience.APPROVER
      ? `/my-team/approvals?timesheet=${encodeURIComponent(id)}`
      : '/timesheet/history',
  [NotificationSubjectType.TIMESHEET_WEEK]: (id, item) =>
    item.audience === NotificationAudience.APPROVER
      ? `/my-team/approvals?timesheet=${encodeURIComponent(id)}`
      : '/timesheet',
  // An `EMPLOYEE` subject is the reader themselves only when the copy was
  // written for them. An APPROVER gets these about someone ELSE (they were made
  // that person's leave approver, or a probation review is due), and this portal
  // has no screen for another employee — so those stay non-clickable rather than
  // opening the reader's own profile.
  // A "Needs attention" ping to a manager about a report opens that report
  // (they are in the manager's team, or the ping would not reach them).
  // An automation notice about a report (e.g. a pay change) opens that report.
  [NotificationSubjectType.EMPLOYEE]: (id, item) =>
    item.type === NotificationType.AUTOMATION_NOTICE
      ? `/my-team/members/${encodeURIComponent(id)}`
      : item.audience === NotificationAudience.SUBJECT
        ? '/my-information'
        : item.type === NotificationType.ATTENTION_PING
          ? `/my-team/members/${encodeURIComponent(id)}`
          : null,
  [NotificationSubjectType.EMPLOYEE_NOTE]: () => '/notes',
  // "Connect your calendar" (a recruiter asked): My calendar.
  [NotificationSubjectType.CALENDAR_CONNECTION]: () => '/calendar-connection',
  // HR shared a candidate: the share in Candidates to review.
  [NotificationSubjectType.CANDIDATE_SHARE]: (id) =>
    `/candidate-reviews/${encodeURIComponent(id)}`,
  [NotificationSubjectType.HR_NOTE]: () => '/notes',
  [NotificationSubjectType.EMPLOYEE_DOCUMENT]: () =>
    '/my-information/documents',
  // HR asked for a document (or a reminder of it): the Documents page, which
  // lists what HR requested, on that request.
  [NotificationSubjectType.DOCUMENT_REQUEST]: (id) =>
    `/my-information/documents?request=${encodeURIComponent(id)}`,
  // A document HR sent to read and confirm (or its reminder): that document.
  [NotificationSubjectType.DOCUMENT_ACKNOWLEDGEMENT]: (id) =>
    `/signatures/read/${encodeURIComponent(id)}`,
  // A document to sign (or its reminder, withdrawal or signed copy): My
  // documents to sign, on that request. Never a signing link.
  [NotificationSubjectType.SIGNATURE_REQUEST]: (id) =>
    `/signatures?tab=to_sign&request=${encodeURIComponent(id)}`,
  // A training of mine (assigned, due soon, overdue, reminder): its player.
  // A manager's notice about a report's overdue training: My Team.
  [NotificationSubjectType.TRAINING]: (id, item) =>
    item.context?.audienceRole === 'manager'
      ? '/my-team'
      : `/trainings/${encodeURIComponent(id)}`,
  // HR approved or refused one of my external trainings: My trainings,
  // External trainings section.
  [NotificationSubjectType.TRAINING_EXTERNAL]: () => '/trainings?tab=external',
  [NotificationSubjectType.COMPANY_FILE]: () => '/files/all',
  [NotificationSubjectType.COMPANY_FOLDER]: (id) => `/files/${id}`,
  [NotificationSubjectType.EMPLOYEE_ASSET]: () => '/my-information/assets',
  [NotificationSubjectType.BANK_ACCOUNT]: () => '/my-information/banking',
  // A decision on the reader's own request opens that section's Requests view.
  // A manager's notice (change rules): a change to decide opens it in My team
  // approvals, a change already saved opens that report's My team page.
  [NotificationSubjectType.EMPLOYEE_CHANGE_REQUEST]: (id, item) => {
    if (item.context?.audienceRole === 'manager') {
      const employeeId = item.context?.employeeId;
      // Only a pending request is one to decide; a saved one (applied at
      // once, or approved and the manager notified) opens the member.
      if (item.context?.status !== EmployeeChangeRequestStatus.Pending)
        return typeof employeeId === 'string'
          ? `/my-team/members/${encodeURIComponent(employeeId)}`
          : '/my-team';
      return `/my-team/approvals?changeRequest=${encodeURIComponent(id)}`;
    }
    const page = CHANGE_REQUEST_PAGE[item.context?.section as EmployeeSection];
    return page ? `${page}?${DETAILS_REQUESTS_VIEW_PARAM}=requests` : null;
  },
};

/** `null` means "render, but don't make it clickable". */
export const resolveNotificationHref = (item: NotificationItem) =>
  resolveHref(item, SUBJECT_ROUTE);
